import { forwardRef, type Ref, type ReactNode, type HTMLAttributes } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../../core/_internal/factory.js';
import './Overlay.css';

export interface OverlayProps extends BaseProps, Omit<HTMLAttributes<HTMLDivElement>, 'color'> {
  /** Background color of the backdrop. Default: themed translucent black. */
  backgroundOpacity?: number;
  color?: string;
  blur?: number;
  /** Fixed-position backdrop covering the viewport. */
  fixed?: boolean;
  /** Honors `useReducedMotion` automatically (fade-in). */
  withFade?: boolean;
  children?: ReactNode;
}

/** Backdrop used behind Modal/Drawer/Dialog. Renders a single div. */
export const Overlay = forwardRef(function Overlay(
  { backgroundOpacity = 0.55, color = '#000', blur, fixed = true, withFade = true, children, ...rest }: OverlayProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const base = useBaseAttrs('rr-overlay', rest, [
    fixed && 'rr-overlay--fixed',
    withFade && 'rr-overlay--fade'
  ]);
  const { dom } = splitBaseProps(rest);
  return (
    <div {...dom}
      ref={ref}
      {...base.attrs}
      className={base.className}
      style={{
        ...base.style,
        backgroundColor: color,
        opacity: backgroundOpacity,
        backdropFilter: blur ? `blur(${blur}px)` : undefined,
        WebkitBackdropFilter: blur ? `blur(${blur}px)` : undefined
      }}
    >
      {children}
    </div>
  );
});
